"use client";

import {memo} from "react";

import {type AdvancedFiltersInstance} from "@/features/rental";

import {useTranslation} from "@/shared/i18n";
import {ButtonBase} from "@/shared/ui/buttons";
import {ButtonSize} from "@/shared/ui/buttons/button-base";
import {CheckboxField, RangeField} from "@/shared/ui/form/fields";

import "./styles/advanced-filters.scss";

// Separate Actions component for reuse
interface AdvancedFiltersActionsProps {
    onClear: () => void;
    onUpdate: () => void;
    buttonSize?: ButtonSize;
}

export const AdvancedFiltersActions = memo(function AdvancedFiltersActions({onClear, onUpdate, buttonSize = "sm"}: AdvancedFiltersActionsProps) {
    const {t} = useTranslation("common");
    return (
        <div className="advanced-filters__actions">
            <ButtonBase weight="normal" variant="secondary" size={buttonSize} onClick={onClear}>
                {t("clear")}
            </ButtonBase>
            <ButtonBase weight="normal" variant="primary" size={buttonSize} onClick={onUpdate}>
                {t("update")}
            </ButtonBase>
        </div>
    );
});

interface AdvancedFiltersViewProps {
    model: AdvancedFiltersInstance;
    onUpdate: () => void;
    onClose: () => void;
    showActions?: boolean; // control whether to show actions inline
}

export const AdvancedFiltersView = memo(function AdvancedFiltersView({model, onUpdate, onClose, showActions = true}: AdvancedFiltersViewProps) {
    const {t} = useTranslation("filters");
    const {t: tCommon} = useTranslation("common");

    const handleUpdate = (): void => {
        onUpdate();
        onClose();
    };

    const handleClear = (): void => {
        model.clear();
        onUpdate();
    };

    return (
        <div className="advanced-filters">
            <div className="advanced-filters__content">
                {/* Rooms Section */}
                <div className="advanced-filters__section">
                    <h3 className="advanced-filters__section-title">{t("rooms")}</h3>

                    <div className="advanced-filters__range-group">
                        <RangeField
                            model={model.bedrooms}
                            labelPosition="left"
                            size="sm"
                            minPlaceholder={tCommon("min")}
                            maxPlaceholder={tCommon("max")}
                        >
                            {t("bedroom")}
                        </RangeField>

                        <RangeField
                            model={model.bathrooms}
                            labelPosition="left"
                            size="sm"
                            minPlaceholder={tCommon("min")}
                            maxPlaceholder={tCommon("max")}
                        >
                            {t("bathrooms")}
                        </RangeField>
                    </div>
                </div>

                {/* Nightly Rate Section */}
                <div className="advanced-filters__section">
                    <RangeField
                        model={model.nightlyRate}
                        minPlaceholder={tCommon("min")}
                        maxPlaceholder={tCommon("max")}
                        className="advanced-filters__nightly-rate"
                    >
                        {t("nightlyRate")}
                    </RangeField>
                </div>

                {/* Type of Property Section */}
                {model.propertyTypes.length > 0 && (
                    <div className="advanced-filters__section">
                        <h3 className="advanced-filters__section-title">{t("typeOfProperty")}</h3>

                        <div className="advanced-filters__checkbox-grid">
                            {model.propertyTypes.map((propertyType) => (
                                <CheckboxField
                                    key={propertyType.type}
                                    model={propertyType.checkbox}
                                    label={propertyType.displayName}
                                    size="sm"
                                    className="advanced-filters__checkbox-item"
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Amenities Section */}
                {model.amenities.length > 0 && (
                    <div className="advanced-filters__section">
                        <h3 className="advanced-filters__section-title">{t("amenities")}</h3>

                        <div className="advanced-filters__checkbox-grid">
                            {model.amenities.map((amenity) => (
                                <CheckboxField
                                    key={amenity.id}
                                    model={amenity.checkbox}
                                    label={amenity.displayName}
                                    size="sm"
                                    className="advanced-filters__checkbox-item"
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Actions - sticky footer */}
            {showActions && <AdvancedFiltersActions onClear={handleClear} onUpdate={handleUpdate} />}
        </div>
    );
});
